<script>import { createEventDispatcher } from "svelte";
import { fade } from "svelte/transition";
import Loading from "../Loading/Loading.svelte";
import Resizer from "../Resizer/Resizer.svelte";
export let src;
export let title = null;
export let thumbnail = null;
export let thumbnailWidth = 0;
export let thumbnailHeight = 0;
export let hideIfPageNotVisible = false;
export let facadeModeEnabled = false;
export let facadeHoverTrigger = false;
export let autoplay = false;
export let height = null;
export let width = null;
export let short = false;
export let disableColtrols = false;
export let legacyLoader = false;
let validSrc = false;
const validSrcHosts = ["www.youtube.com", "youtube.com"];
const checkSrc = (src2) => {
  if (!src2) {
    return false;
  }
  try {
    const srcUrl = new URL(src2);
    if (!validSrcHosts.includes(srcUrl.host)) {
      return false;
    }
    return true;
  } catch (e) {
    console.error(e);
    return false;
  }
};
$:
  validSrc = checkSrc(src);
$:
  videoId = validSrc ? getVideoId(src) : "";
let loaded = false;
let hover = false;
let clicked = false;
let hoverEnter = false;
let frame = null;
let played = false;
$:
  origin = validSrc ? new URL(src).origin : "";
$:
  loading = (clicked || !facadeModeEnabled || facadeModeEnabled && facadeHoverTrigger && hoverEnter || !thumbnail) && !loaded;
const dispatch = createEventDispatcher();
const load = () => {
  loaded = true;
  dispatch("iframeLoaded");
  if (frame) {
    frame.contentWindow?.postMessage(
      `{"event":"listening","id":${videoId},"channel":"widget"}`,
      origin
    );
  }
};
let pageVisible = true;
const setPageVisible = () => {
  if (document.visibilityState === "hidden" && hideIfPageNotVisible) {
    pageVisible = false;
  } else {
    pageVisible = true;
  }
};
const getVideoId = (src2) => {
  if (!src2) {
    return 0;
  }
  try {
    const videoUrl = new URL(src2);
    const videoKey = videoUrl.pathname.split("/").pop() ?? "";
    return Number(
      videoKey.split("").map((l) => l.charCodeAt(0).toString()).join("")
    );
  } catch (e) {
    return 0;
  }
};
const getVideoUrl = (src2, autoplay2) => {
  if (!src2) {
    return "";
  }
  try {
    const videoUrl = new URL(src2);
    const playerOrigin = window?.location?.href ? new URL(window.location.href).origin : null;
    if (playerOrigin) {
      videoUrl.searchParams.append("origin", playerOrigin);
    }
    if (autoplay2) {
      videoUrl.searchParams.append("autoplay", "1");
    }
    if (disableColtrols) {
      videoUrl.searchParams.append("controls", "0");
    }
    videoUrl.searchParams.append("enablejsapi", "1");
    return videoUrl.toString();
  } catch (e) {
    return "";
  }
};
const messageListener = (event) => {
  if (videoId !== 0 && event?.origin === origin && event?.data && typeof event.data === "string") {
    try {
      const eventData = JSON.parse(event.data);
      if (eventData?.id === videoId) {
        if (eventData?.info?.playerState === 1) {
          if (played === false) {
            played = true;
            dispatch("videoPlayed");
          }
        }
      }
    } catch (error) {
      console.error("[Player]", error);
    }
  }
};
</script>

<style>@keyframes pulse {
  50% {
    transform: scale(1);
  }
  100% {
    transform: scale(1.2);
  }
}
@keyframes rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
iframe {
  background: #000000;
  aspect-ratio: 16/9;
  overflow: hidden;
  width: 100%;
  transition: opacity 0.3s ease-out;
  opacity: 0;
  border: none;
  border-radius: 8px;
}
iframe.loaded {
  opacity: 1;
}
.listener {
  width: 100%;
  height: 100%;
  cursor: pointer;
  position: relative;
}
.loading::after {
  position: absolute;
  left: calc(50% - 24px);
  top: calc(50% - 24px);
  z-index: -1;
  content: " ";
  display: block;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border-top: 8px solid hsl(var(--color-accent));
  border-bottom: 8px solid hsl(var(--color-accent));
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  animation: rotate 1.5s steps(45) infinite;
}
.placeholder {
  background: transparent;
  aspect-ratio: 16/9;
  width: 100%;
  background-size: cover;
  background-position: center center;
  display: flex;
  justify-content: center;
  align-items: center;
}
.placeholder.short {
  aspect-ratio: 9/16;
}
.play-outer {
  background-color: rgba(46, 39, 63, 0.4);
  border-radius: 50%;
  padding: 18px;
  transition: all 0.3s ease-in-out;
}
.play-outer.hover {
  transform: scale(1.8);
}
.play-outer::after {
  content: " ";
  width: 100%;
  height: 100%;
  background-color: hsl(var(--color-accent));
  position: absolute;
  left: 0;
  top: 0;
  border-radius: 50%;
  opacity: 0.2;
  transform: scale(0);
  transition: all 0.3s ease-in-out;
}
.play-outer.hover::after {
  transform: scale(1.2);
  animation: pulse 1s linear infinite;
  mix-blend-mode: darken;
}
.play-inner {
  display: block;
  width: 0;
  height: 0;
  border-top: 17px solid transparent;
  border-left: 36px solid white;
  border-bottom: 17px solid transparent;
  margin-right: -6px;
}</style>

<svelte:document on:visibilitychange={setPageVisible} />
<svelte:window on:message={messageListener} />

<div
  role="none"
  class="listener"
  class:loading={loading && legacyLoader}
  on:click={() => {
    clicked = true;
  }}
  on:mouseenter={() => {
    if (!hover && facadeHoverTrigger && !hoverEnter) {
      dispatch("videoHover");
    }

    if (!hoverEnter) {
      hoverEnter = true;
    }

    hover = true;
  }}
  on:mouseleave={() => {
    if (!facadeHoverTrigger) {
      hover = false;
    }
  }}>
  {#if clicked || !facadeModeEnabled || (facadeModeEnabled && facadeHoverTrigger && hover) || !thumbnail}
    {#if pageVisible}
      {#if loading && !legacyLoader}
        <div class="absolute flex h-full w-full items-center justify-center">
          <Loading size={40} />
        </div>
      {/if}
      <iframe
        {title}
        src={getVideoUrl(
          validSrc ? src : "",
          autoplay || (facadeModeEnabled && !facadeHoverTrigger),
        )}
        allow="fullscreen {origin}"
        scrolling="no"
        class:loaded
        on:load={load}
        {width}
        {height}
        bind:this={frame} />
    {/if}
  {:else}
    <Resizer src={thumbnail} w={thumbnailWidth || 0} h={thumbnailHeight || 0} let:at1x>
      <div
        transition:fade
        class="placeholder"
        class:short
        style="background-image:url({at1x})"
        {title}>
        <slot name="player-button" {hover}>
          <span class="play-outer" class:hover>
            <span class="play-inner"></span>
          </span>
        </slot>
      </div>
    </Resizer>
  {/if}
</div>
